import React, {useState} from 'react';
import {Switch, Route, useRouteMatch, useHistory} from 'react-router-dom';
import PrivateRoute from '../PrivateRoute';
import RegisterVerifyEmail from './RegisterVerifyEmail';
import RegisterPersonalInfo from './RegisterPersonalInfo';
import RegisterSocialProfile from './RegisterSocialProfile';
import RegisterMilitaryService from '../../../../shared/project-info/RegisterMilitaryService';
import RegisterPrivacyWaiver from './RegisterPrivacyWaiver';
import RegisterWorkSpaceAccessCode from './RegisterWorkSpaceAccessCode';
import TextInput from '../TextInput';
import CustomButton from '../CustomButton';
import styles from './Registration.module.css';
import {rwbApi} from '../../../../shared/apis/api';
import {userProfile} from '../../../../shared/models/UserProfile';
import {isNullOrEmpty, validateEmail} from '../../../../shared/utils/Helpers';
import Loading from '../Loading';
import {logCreateAccount} from '../../../../shared/models/Analytics';
import {projectConfig} from '../../../../shared/project-info/projectConfig';
import registerProfileFlow from '../../../../shared/utils/RegisterProfileFlow';

export default function Registration() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [firstNameErrorText, setFirstNameErrorText] = useState('');
  const [lastNameErrorText, setLastNameErrorText] = useState('');
  const [emailErrorText, setEmailErrorText] = useState('');
  const [confirmEmailErrorText, setConfirmEmailErrorText] = useState('');
  const [passwordErrorText, setPasswordErrorText] = useState('');
  const [confirmPasswordErrorText, setConfirmPasswordErrorText] = useState('');
  const [serverErrorText, setServerErrorText] = useState('');

  const match = useRouteMatch();
  const history = useHistory();

  const clearErrorWarnings = () => {
    setFirstNameErrorText('');
    setLastNameErrorText('');
    setEmailErrorText('');
    setConfirmEmailErrorText('');
    setPasswordErrorText('');
    setConfirmPasswordErrorText('');
    setServerErrorText('');
  };

  const createAccountPressed = () => {
    clearErrorWarnings();

    //Local error checking

    let field_is_required_string = 'THIS FIELD IS REQUIRED';
    let hasError = false;

    if (isNullOrEmpty(firstName)) {
      setFirstNameErrorText(field_is_required_string);
      hasError = true;
    }
    if (!projectConfig.allowBlankLastName && isNullOrEmpty(lastName)) {
      setLastNameErrorText(field_is_required_string);
      hasError = true;
    }
    if (isNullOrEmpty(email)) {
      setEmailErrorText(field_is_required_string);
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailErrorText('PLEASE ENTER A VALID EMAIL');
      hasError = true;
    }
    if (isNullOrEmpty(confirmEmail)) {
      setConfirmEmailErrorText(field_is_required_string);
      hasError = true;
    }
    if (email !== confirmEmail) {
      setConfirmEmailErrorText('EMAIL DOES NOT MATCH');
      hasError = true;
    }
    if (isNullOrEmpty(password)) {
      setPasswordErrorText(field_is_required_string);
      hasError = true;
    }
    if (!isNullOrEmpty(password) && password.length < 8) {
      setPasswordErrorText('PASSWORD MUST BE AT LEAST 8 CHARACTERS LONG');
      hasError = true;
    }
    if (isNullOrEmpty(confirmPassword)) {
      setConfirmPasswordErrorText(field_is_required_string);
      hasError = true;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordErrorText('PASSWORD DOES NOT MATCH');
      hasError = true;
    }

    if (hasError) return;
    logCreateAccount();
    createUser(firstName, lastName, email, password);
  };

  const createUser = (firstName, lastName, email, password) => {
    setIsLoading(true);
    return rwbApi
      .createNewUser(
        JSON.stringify({
          grant_type: 'client_credentials',
          first_name: firstName,
          last_name: lastName,
          email: email,
          password: password,
        }),
      )
      .then((response) => {
        if (response.status === 200) {
          return response.json().then((jsonBody) => {
            setServerErrorText('');
            userProfile.setUserId(jsonBody.id);
            return rwbApi
              .loginUser(email, password)
              .then((response) => {
                rwbApi.putUser(
                  JSON.stringify({registration_started_via_app: true}),
                );
                history.push(`${match.path}/confirm_email`);
              })
              .catch((error) => {
                alert(
                  `Server error. Please try again. If the problem persists, please contact ${projectConfig.supportEmail}`,
                );
                setIsLoading(false);
              });
          });
        } else {
          return response.json().then((jsonBody) => {
            setIsLoading(false);
            const emailAlreadyExists =
              jsonBody?.code === 'existing_user_login' ||
              (Array.isArray(jsonBody?.email) &&
                jsonBody.email[0]?.includes('already exists'));
            if (emailAlreadyExists) {
              setEmailErrorText('AN ACCOUNT WITH THIS EMAIL ALREADY EXISTS');
            } else if (Array.isArray(jsonBody?.email) && jsonBody.email[0]) {
              setEmailErrorText(jsonBody.email[0].toUpperCase());
            } else {
              setServerErrorText(
                'THERE WAS A PROBLEM WITH THE SERVER. PLEASE TRY AGAIN',
              );
            }
          });
        }
      })
      .catch((err) => {
        console.warn(err);
        setIsLoading(false);
        setServerErrorText(
          'THERE WAS A PROBLEM WITH THE SERVER, PLEASE TRY AGAIN',
        );
      });
  };

  // Maps the screen names at projectConfig.registerProfileSteps to web components.
  const registerProfileComponentMap = {
    PersonalInfo: RegisterPersonalInfo,
    SocialProfile: RegisterSocialProfile,
    MilitaryService: RegisterMilitaryService,
    PrivacyWaiver: RegisterPrivacyWaiver,
  };

  const totalSteps = registerProfileFlow.getTotalSteps();
  return (
    <div>
      <Switch>
        <PrivateRoute
          path={`${match.path}/confirm_email`}
          component={RegisterVerifyEmail}
        />
        <PrivateRoute
          path={`${match.path}/work_space`}
          component={RegisterWorkSpaceAccessCode}
        />

        {/* Dynamically create the profile registration routes. The order and presense of these next steps are set in projecConfig.registerProfileSteps */}
        {projectConfig.registerProfileSteps.map((step) => (
          <PrivateRoute
            key={step.path}
            path={`${match.path}/${step.path}`}
            component={(props) =>
              React.createElement(registerProfileComponentMap[step.screen], {
                ...props,
                currentPath: step.path,
              })
            }
          />
        ))}

        {/* Next route is public -- they need to create an account before they can auth! */}
        <Route path={match.path}>
          <Loading size={100} color={'var(--white)'} loading={isLoading} />
          <div className={styles.container}>
            <div className={styles.headerContainer}>
              <h3 className="title">Signup</h3>
              <p className="titleSubheader">Step 1 of {totalSteps}</p>
            </div>
            <div className={styles.contentContainer}>
              <div className={styles.formContainer}>
                <TextInput
                  label={projectConfig.FirstNameLabel}
                  error={firstNameErrorText}
                  value={firstName}
                  onValueChange={(text) => setFirstName(text.target.value)}
                  onEnter={createAccountPressed}
                  autoFillLabel="given-name"
                />
                <TextInput
                  label={projectConfig.LastNameLabel}
                  error={lastNameErrorText}
                  value={lastName}
                  onValueChange={(text) => setLastName(text.target.value)}
                  onEnter={createAccountPressed}
                  autoFillLabel="family-name"
                />
                <TextInput
                  label={'Email'}
                  type={'email'}
                  error={emailErrorText}
                  value={email}
                  onValueChange={(text) => setEmail(text.target.value)}
                  onEnter={createAccountPressed}
                />
                {!emailErrorText && <p>{projectConfig.emailAddressMessage}</p>}
                <TextInput
                  label={'Confirm Email'}
                  type={'email'}
                  error={confirmEmailErrorText}
                  value={confirmEmail}
                  onValueChange={(text) => setConfirmEmail(text.target.value)}
                  onEnter={createAccountPressed}
                />
                <TextInput
                  label={'Password'}
                  type={'password'}
                  error={passwordErrorText}
                  value={password}
                  onValueChange={(text) => setPassword(text.target.value)}
                  onEnter={createAccountPressed}
                />
                <TextInput
                  label={'Confirm Password'}
                  type={'password'}
                  error={confirmPasswordErrorText}
                  value={confirmPassword}
                  onValueChange={(text) =>
                    setConfirmPassword(text.target.value)
                  }
                  onEnter={createAccountPressed}
                />
              </div>
              <div className={styles.privacyPolicyContainer}>
                <p>
                  By creating an account, you agree to{' '}
                  {`${projectConfig.name}'s `}
                  <a
                    className={`link ${styles.clickable}`}
                    target="_blank"
                    rel="noopener"
                    href={projectConfig.privacyPolicy}>
                    Privacy Policy
                  </a>
                  {/* &nbsp;and&nbsp; */}
                  {/* <a
                    className={`link ${styles.clickable}`}
                    target="_blank"
                    rel="noopener"
                    href={projectConfig.communityGuidelines}>
                    Community Guidelines
                  </a> */}
                </p>
              </div>
              {serverErrorText != '' && (
                <p className={`errorMessage ${styles.centerText}`}>
                  {serverErrorText}
                </p>
              )}
              <div className={styles.buttonContainer}>
                <CustomButton
                  onClick={createAccountPressed}
                  label="Create Account"
                  buttonStyle="primary"
                />
                <CustomButton
                  link={true}
                  to="/login"
                  label="Have an account? Login Here"
                  buttonStyle="secondary"
                />
              </div>
            </div>
          </div>
        </Route>
      </Switch>
    </div>
  );
}
